// @flow
import Radio from "backbone.radio";
import lpad from "lodash-es/padStart";

import { Bloodhound } from "common/components/typeahead";

import { store } from "common/store";
import strings from "common/strings";

import {
    FETCH_MAPPINGS_IF_MISSING
} from "common/actions/common";

import { references } from "common/helpers/references";
import { getCurrencyName } from "common/helpers/currency";

import { mcc_categories } from "./mcc_categories";

const createTemplate = (suggestionTemplate = (data) => `<strong>${data.alias}</strong> – ${data.typeString}`) => (data) => `<div>
                <span class="exclude" data-toggle="tooltip" data-placement="top" title="${strings.excludeFilter}"><i class="far fa-minus icon" aria-hidden="true"></i></span>
                <span class="include" data-toggle="tooltip" data-placement="top" title="${strings.includeFilter}"><i class="far fa-plus icon" aria-hidden="true"></i></span>
                <span class="suggestion">${suggestionTemplate(data)}</span>
            </div>`;

export type TypeaheadSourceDataType = {
    pivot: string,
    value: string,
    typeString: string,
    alias: string
};

export type AnalyticsSectionDimensionsType = { [DimensionType]: Array<any> };

type TypeaheadSourceOptionsType = {
    data: Array<TypeaheadSourceDataType>,
    display: Array<"value" | "alias">,
    templates_suggestion: () => string,
    queryTokenizer?: Function,
    datumTokenizer?: Function
};

const makeDimensionHandler = function(pivot: DimensionNameType, { display = ["alias"], translate_alias = true } = {}): (AnalyticsSectionDimensionsType) => TypeaheadSourceOptionsType {
    return function(values): TypeaheadSourceOptionsType {
        let data;
        if (Array.isArray(values)) {
            data = values.map((value): TypeaheadSourceDataType => ({
                pivot,
                value,
                alias: translate_alias ? references.toAlias(value, pivot) : value,
                typeString: Radio.channel("main").request("get:getters").findPivotName(pivot)
            }));
        } else {
            data = Object.entries(values).map(([key, value]): TypeaheadSourceDataType => ({
                pivot,
                value: key,
                alias: value,
                typeString: Radio.channel("main").request("get:getters").findPivotName(pivot)
            }));
        }
        
        return {
            data,
            display,
            templates_suggestion: createTemplate()
        };
    };
};

const handlers: { [dimension: string]: (AnalyticsSectionDimensionsType) => TypeaheadSourceOptionsType } = {
    currency_alias(values): TypeaheadSourceOptionsType {
        // CURRENCY TYPEAHEAD
        const data = values.map((currency: string): TypeaheadSourceDataType => ({
            pivot: "currency_alias",
            value: currency,
            alias: getCurrencyName(currency),
            typeString: Radio.channel("main").request("get:getters").findPivotName("currency_alias")
        }));
        
        // Currencies should be searchable by both code and alias
        return {
            data,
            display: ["value", "alias"],
            templates_suggestion: createTemplate(({ alias, value, typeString }) => `<strong>${alias}</strong> (${value}) – ${typeString}`)
        };
    },
    
    purchase_currency(values): TypeaheadSourceOptionsType {
        // CURRENCY TYPEAHEAD
        const data = values.map((currency: string): TypeaheadSourceDataType => ({
            pivot: "purchase_currency",
            value: currency,
            alias: getCurrencyName(currency),
            typeString: Radio.channel("main").request("get:getters").findPivotName("purchase_currency")
        }));
        
        // Currencies should be searchable by both code and alias
        return {
            data,
            display: ["value", "alias"],
            templates_suggestion: createTemplate(({ alias, value, typeString }) => `<strong>${alias}</strong> (${value}) – ${typeString}`)
        };
    },
    
    provider: makeDimensionHandler("provider"),
    
    acquirer: makeDimensionHandler("acquirer"),
    
    gateway: makeDimensionHandler("gateway"),
    
    provider_path: makeDimensionHandler("provider_path"),
    
    method: makeDimensionHandler("method"),
    
    reason_code(values): TypeaheadSourceOptionsType {
        const data = Object.entries(values).map(([key, value]): TypeaheadSourceDataType => ({
            pivot: "reason_code",
            value: key,
            // $FlowFixMe Flow doesn't understand return type on Object.entries https://github.com/facebook/flow/issues/2221
            alias: value.description,
            typeString: Radio.channel("main").request("get:getters").findPivotName("reason_code")
        }));
        
        return {
            data,
            display: ["value", "alias"],
            templates_suggestion: createTemplate(({ alias, value, typeString }) => `${value} - <small>${alias}</small> – ${typeString}`)
        };
    },
    
    response_code(values): TypeaheadSourceOptionsType {
        return {
            data: Object.entries(values).map(([key, value]) => ({
                pivot: "response_code",
                value: key,
                // $FlowFixMe Flow doesn't understand return type on Object.entries https://github.com/facebook/flow/issues/2221
                alias: value.description,
                typeString: Radio.channel("main").request("get:getters").findPivotName("response_code")
            })),
            display: ["alias"],
            templates_suggestion: createTemplate(({ alias, typeString }) => `<strong><small>${alias}</small></strong> – ${typeString}`)
        };
    },
    
    card: makeDimensionHandler("card", { translate_alias: false }),
    
    category: makeDimensionHandler("category", { translate_alias: false }),
    
    country: makeDimensionHandler("country"),
    
    auths_attempts: makeDimensionHandler("auths_attempts"),
    
    mcc(values, { my = true, filter = () => true } = {}): TypeaheadSourceOptionsType {
        // MCCs
        let normalizedValues;
        if (my) {
            normalizedValues = values.reduce((memo, val) => {
                // Filter out empty values
                if (val && filter(val)) {
                    return {
                        ...memo,
                        [val]: references.toAlias(val, "mcc")
                    };
                }
                
                return memo;
            }, {});
        } else {
            normalizedValues = Object.entries(references.getAliasGroupByType("mcc")).reduce((memo, [key, val]) => {
                if (filter(key)) {
                    return {
                        ...memo,
                        [key]: val.description
                    };
                }
                
                return memo;
            }, {});
        }
        
        const data = Object.entries(normalizedValues).map(([key, value]): TypeaheadSourceDataType => ({
            pivot: "mcc",
            value: key,
            // $FlowFixMe Flow doesn't understand return type on Object.entries https://github.com/facebook/flow/issues/2221
            alias: value,
            typeString: Radio.channel("main").request("get:getters").findPivotName("mcc")
        }));
        
        // MCC should be searchable by both code and alias
        return {
            data,
            display: ["value", "alias"],
            templates_suggestion: createTemplate(({ value, alias, typeString }) => `<strong>${value}</strong> ${alias} – ${typeString}`)
        };
    },
    
    cycle(values): TypeaheadSourceOptionsType {
        // Chargeback lifecycle states
        const cycles = references.getAliasGroupByType("cycle");
        
        if (cycles === null) {
            throw new Error("Undefined cbk cycles");
        }
        
        const data = Object.entries(cycles.all).map(([key, value]): TypeaheadSourceDataType => ({
            pivot: "cycle",
            value: key,
            // $FlowFixMe Flow doesn't understand return type on Object.entries https://github.com/facebook/flow/issues/2221
            alias: value,
            typeString: Radio.channel("main").request("get:getters").findPivotName("cycle")
        }));
        
        return {
            data,
            display: ["alias"],
            templates_suggestion: createTemplate()
        };
    },
    
    bu(): TypeaheadSourceOptionsType {
        // BUs
        const business_units = references.getAliasGroupByType("bu");
        
        const data = Object.entries(business_units).map(([key, value]) => ({
            pivot: "bu",
            value: key,
            // $FlowFixMe Flow doesn't understand return type on Object.entries https://github.com/facebook/flow/issues/2221
            alias: value.display_name,
            // $FlowFixMe Flow doesn't understand return type on Object.entries https://github.com/facebook/flow/issues/2221
            display: `<b>${value.display_name}</b>${value.parent ? ` - ${value.parent}` : ""}`,
            parent: value.parent,
            typeString: Radio.channel("main").request("get:getters").findPivotName("bu")
        }));
        
        // BUs should be searchable by alias
        return {
            data,
            display: ["alias"],
            datumTokenizer(datum) {
                // Add all words from the label
                const words = Bloodhound.tokenizers.whitespace(datum.alias);
                const tokens = [...words];
                
                try {
                    // Then loop through the words and if word is wrapped in special characters then unwrap it and push to tokens.
                    // Example, if BU label is `Store #1234 (4567788)` then 4567788 should be a token as well.
                    const matchRegex = /\((.*?)\)/;
                    words.forEach((w) => {
                        const match = w.match(matchRegex);
                        if (match && match[1]) {
                            tokens.push(match[1]);
                        }
                    });
                } catch {
                    // Ignore the error
                }
                
                return tokens;
            },
            templates_suggestion: createTemplate(({ display, typeString }) => `${display} – ${typeString}`)
        };
    },
    
    mid(values): TypeaheadSourceOptionsType {
        // MIDs
        const data = values.map((value: string): TypeaheadSourceDataType => ({
            pivot: "mid",
            value,
            alias: value,
            typeString: Radio.channel("main").request("get:getters").findPivotName("mid")
        }));
        
        // Mids should be searchable by both code and alias
        return {
            data,
            display: ["alias"],
            datumTokenizer(datum: TypeaheadSourceDataType): Array<string> {
                const stringSize = datum.alias.length;
                // Add actual string to the tokens
                const tokens = [datum.alias];
                
                // multiple combinations for every available size
                // (eg. 1234567 = 567, 4567, 34567, 234567)
                for (let size = 3; size <= Math.min(6, stringSize); size++) {
                    tokens.push(datum.alias.substr(stringSize - size, stringSize));
                }
                
                return tokens;
            },
            templates_suggestion: createTemplate()
        };
    },
    
    response_type: makeDimensionHandler("response_type", {
        display: ["value", "alias"]
    }),
    
    bank: makeDimensionHandler("bank", { translate_alias: false }),
    
    fee_type: makeDimensionHandler("fee_type"),
    
    fee_detail(values): TypeaheadSourceOptionsType {
        return {
            data: values.map((value: string): TypeaheadSourceDataType => ({
                pivot: "fee_detail",
                value,
                alias: value,
                typeString: Radio.channel("main").request("get:getters").findPivotName("fee_detail")
            })),
            display: ["value"],
            datumTokenizer(datum: TypeaheadSourceDataType): Array<string> {
                return [datum.alias, ...Bloodhound.tokenizers.nonword(datum.alias)];
            },
            templates_suggestion: createTemplate()
        };
    },
    
    transaction_src: makeDimensionHandler("transaction_src"),
    
    digital_wallet: makeDimensionHandler("digital_wallet"),
    
    terminal_type: makeDimensionHandler("terminal_type"),
    
    entry_method: makeDimensionHandler("entry_method"),
    
    auth_method: makeDimensionHandler("auth_method"),
    
    // invoice_no: getDimensionHandler("invoice_no"),
    
    auths_tests: makeDimensionHandler("auths_tests"),
    
    stl_status: makeDimensionHandler("stl_status"),
    
    stl_type: makeDimensionHandler("stl_type"),
    
    avs_response_code: makeDimensionHandler("avs_response_code"),
    
    cvv2_response_code: makeDimensionHandler("cvv2_response_code"),
    
    psd2: makeDimensionHandler("psd2"),
    
    fraud_action_type: makeDimensionHandler("fraud_action_type"),
    
    fraud_program: makeDimensionHandler("fraud_program"),
    
    fraud_reason_code: makeDimensionHandler("fraud_reason_code"),
    
    EUR_amount_bucket: makeDimensionHandler("EUR_amount_bucket"),
    
    mcc_category: makeDimensionHandler("mcc_category"),
    
    presenter: makeDimensionHandler("presenter"),
    
    store_id: makeDimensionHandler("store_id")
};

export const getDimensionHandler = function(dimension) {
    const customPivots = Radio.channel("main").request("get:getters").getCustomPivots();
    
    if (customPivots.includes(dimension)) {
        return makeDimensionHandler(dimension, { translate_alias: false });
    }
    
    return handlers[dimension];
};

export const getDimensionStringPlural = function(dim: string, alias: string): string {
    const s = Radio.channel("main").request("get:getters").findPivotName(dim, { plural: true }) || Radio.channel("main").request("get:getters").findPivotName(alias, { plural: true });
    
    const sLower = s.toLowerCase();
    if ([strings.mccs.toLowerCase(), strings.mids.toLowerCase()].includes(sLower)) {
        return s;
    }
    return sLower;
};

type getAllPivotsWithValuesParameterType = {
    filter: (Object) => $Keys<Object>[]
};

/**
 * Get needed pivots with values
 * We need both merchant dimensions and zoolander mappings here
 * Pass filter to include only needed pivots
 * filter must return an array with required pivots
 */
export const getAllPivotsWithValues = function({ filter = (all) => Object.keys(all) }: getAllPivotsWithValuesParameterType = {}): Promise<AnalyticsSectionDimensionsType> {
    return Promise.all([
        Radio.channel("main").request("get:merchant").dimensions,
        FETCH_MAPPINGS_IF_MISSING()
    ]).then(([allMerchantDimensions, zoolanderMappings]): AnalyticsSectionDimensionsType => {
        const allZoolanderMappings: Object = {
            response_code: zoolanderMappings.decline_codes,
            reason_code: zoolanderMappings.cbk_codes,
            cycle: zoolanderMappings.cbk_lifecycle,
            mcc_category: zoolanderMappings.mcc_category
        };
        
        const localDimensions = {
            acquirer: references.getAliasGroupByType("acquirer")
        };
        
        if (store.getState().permissions.user_permissions.has_fraud) {
            localDimensions.psd2 = references.getAliasGroupByType("psd2");
            localDimensions["fraud_reason_code"] = references.getAliasGroupByType("fraud_reason_code");
            localDimensions.EUR_amount_bucket = references.getAliasGroupByType("EUR_amount_bucket");
        }
        
        if (store.getState().permissions.user_permissions.feature_authorizations_attempts) {
            localDimensions.auths_attempts = references.getAliasGroupByType("auths_attempts");
        }
        
        if (store.getState().permissions.user_permissions.feature_test_authorizations) {
            localDimensions.auths_tests = references.getAliasGroupByType("auths_tests");
        }
        
        if (store.getState().permissions.user_permissions.data_access_chargebacks) {
            localDimensions.cycle = references.getAliasGroupByType("cycle");
            localDimensions.category = references.getAliasGroupByType("category");
        }
        
        if (store.getState().permissions.user_permissions.data_access_sales_refunds) {
            localDimensions.stl_status = references.getAliasGroupByType("stl_status");
            localDimensions.stl_type = references.getAliasGroupByType("stl_type");
        }
        
        if (store.getState().permissions.user_permissions.data_access_fees) {
            localDimensions.fee_type = references.getAliasGroupByType("fee_type");
        }
        
        if (store.getState().permissions.user_permissions.data_access_authorizations) {
            localDimensions.response_type = references.getAliasGroupByType("response_type");
        }
        
        if (store.getState().permissions.user_permissions.data_access_authorizations || store.getState().permissions.user_permissions.data_access_sales_refunds) {
            localDimensions.cvv2_response_code = references.getAliasGroupByType("cvv2_response_code");
        }
        
        if (store.getState().permissions.user_permissions.data_access_authorizations || store.getState().permissions.user_permissions.data_access_sales_refunds) {
            localDimensions.avs_response_code = references.getAliasGroupByType("avs_response_code");
        }
        
        const allRequiredPivots = filter({
            ...allMerchantDimensions,
            ...allZoolanderMappings,
            ...localDimensions
        });
        
        // Pick needed dimensions and return to the callee
        const allAvailable = {
            ...allMerchantDimensions,
            ...allZoolanderMappings,
            ...localDimensions
        };
        
        return allRequiredPivots.reduce((memo, cur) => {
            if (allAvailable[cur]) {
                return {
                    ...memo,
                    [cur]: allAvailable[cur]
                };
            }
            
            return memo;
        }, {});
    });
};

export const whitelistedPivots = {
    psd2: ["auth_method", "bu", "country", "method", "acquirer", "fraud_reason_code", "card", "entry_method", "EUR_amount_bucket", "psd2_region"],
    fraud: ["auth_method", "bu", "country", "method", "acquirer", "fraud_reason_code", "card", "entry_method", "psd2", "psd2_region"],
    fees: ["fee_type"],
    authorizations: ["response_code", "attempt", "auths_attempts", "auths_tests", "response_type", "avs_response_code", "cvv2_response_code"],
    chargebacks: ["category", "reason_code", "cycle"],
    cashflow: [],
    settlements: ["stl_status", "stl_type", "avs_response_code", "cvv2_response_code"]
};

export const findMccCategory = function(mcc, addRange = true) {
    const mcc_number = Number.parseInt(mcc, 10);
    
    if (Number.isFinite(mcc_number)) {
        const match = mcc_categories.find(({ lower, upper }) => lower <= mcc_number && mcc_number <= upper);
        
        if (match) {
            if (addRange) {
                return `${lpad(match.lower, 4, "0")}-${lpad(match.upper, 4, "0")} ${match.category}`;
            }
            
            return match.category;
        }
    }
    
    return void 0;
};
